import React, { useState, useMemo, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import { useLocation } from "@reach/router";

const WrapperHeader = styled.div`
  margin-top: 10px;

  > div {
    height: 140px;
    border-radius: 10px;
    opacity: 1;

    @media screen and (max-width: 1200px) {
      height: 100px;
    }

    @media screen and (max-width: 640px) {
      height: 80px;
      padding: 10px;
    }
  }
`;

const WrapperContent = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 15px;
  background: ${(props) => props.theme.backgroundHeader};
  box-shadow: -10px -10px 10px #ffffff, 1px 5px 20px 2px #00000080;

  .logo {
    width: 150px;
    height: 150px;
    transform: translate(50%);
    margin: auto;
    user-select: none;
    object-fit: contain;

    @media screen and (max-width: 1200px) {
      width: 145px;
      height: 70px;
      transform: translate(0);
      margin: 0px;
    }

    @media screen and (max-width: 640px) {
      width: 80px;
      height: 50px;
      transform: translate(0);
      margin: 0px;
    }
  }
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 196px;
  height: 52px;
  background: #e8e8e0 0% 0% no-repeat padding-box;
  box-shadow: -5px -5px 10px #ffffff99;
  border-radius: 26px;
  padding: 3px;
  margin: 15px;
  user-select: none;

  @media screen and (max-width: 1200px) {
    margin: 5px;
    width: 135px;
    height: 35px;
  }

  @media screen and (max-width: 640px) {
    margin: 3px;
    width: 115px;
    height: 25px;
  }
`;

const Button = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 186px;
  height: 42px;
  background: ${(props) => props.theme.background};
  border: ${(props) => props.theme.border};
  border-radius: 26px;
  opacity: 1;
  color: ${(props) => props.theme.color};
  font-size: 24px;

  &.active {
    color: ${(props) => props.theme.hoverColor};
    background: ${(props) => props.theme.hoverBackground};
  }

  &:hover {
    color: ${(props) => props.theme.hoverColor};
    background: ${(props) => props.theme.hoverBackground};
  }

  @media screen and (max-width: 1200px) {
    font-size: 18px;
    width: 130px;
    height: 30px;
  }

  @media screen and (max-width: 640px) {
    font-size: 10px;
    width: 110px;
    height: 20px;
  }
`;

const colorHeaderTheme = {
  live22: {
    color: "#0396FF",
    background: "#e8e8e0 0% 0% no-repeat padding-box",
    backgroundHeader:
      "transparent linear-gradient(180deg, #0396ff 0%, #0080db 100%) 0% 0% no-repeat padding-box",
    hoverColor: "#ffffff",
    hoverBackground:
      "transparent linear-gradient(180deg, #0396FF 0%, #0080DB 100%) 0% 0% no-repeat padding-box",
    border: "2px solid #0396FF",
    borderBottom: "15px solid #0396ff",
  },
  slotxo: {
    color: "#E40203",
    background: "#e8e8e0 0% 0% no-repeat padding-box",
    backgroundHeader:
      "transparent linear-gradient(180deg, #E40203 0%, #960E10 100%) 0% 0% no-repeat padding-box",
    hoverColor: "#ffffff",
    hoverBackground:
      "transparent linear-gradient(180deg, #E40203 0%, #960E10 100%) 0% 0% no-repeat padding-box",
    border: "2px solid #E40203",
    borderBottom: "15px solid #0396ff",
  },
  pgslot: {
    color: "#28BC7B",
    background: "#e8e8e0 0% 0% no-repeat padding-box",
    backgroundHeader:
      "transparent linear-gradient(180deg, #28BC7B 0%, #3DB741 100%) 0% 0% no-repeat padding-box",
    hoverColor: "#ffffff",
    hoverBackground:
      "transparent linear-gradient(180deg, #28BC7B 0%, #3DB741 100%) 0% 0% no-repeat padding-box",
    border: "2px solid #28BC7B",
    borderBottom: "15px solid #28BC7B",
  },
  joker: {
    color: "#FF12A4",
    background: "#e8e8e0 0% 0% no-repeat padding-box",
    backgroundHeader:
      "transparent linear-gradient(180deg, #FF12A4 0%, #DA0055 100%) 0% 0% no-repeat padding-box",
    hoverColor: "#ffffff",
    hoverBackground:
      "transparent linear-gradient(180deg, #FF12A4 0%, #DA0055 100%) 0% 0% no-repeat padding-box",
    border: "2px solid #FF12A4",
    borderBottom: "15px solid #FF12A4",
  },
  ambslot: {
    color: "#FFB503",
    background: "#e8e8e0 0% 0% no-repeat padding-box",
    backgroundHeader:
      "transparent linear-gradient(180deg, #FAEB57 0%, #E59801 100%) 0% 0% no-repeat padding-box",
    hoverColor: "#ffffff",
    hoverBackground:
      "transparent linear-gradient(180deg, #FAEB57 0%, #E59801 100%) 0% 0% no-repeat padding-box",
    border: "2px solid #FFB503",
    borderBottom: "15px solid #FFB503",
  },
};

const Header = () => {
  const data = useStaticQuery(graphql`
    query HeaderInfo {
      datoCmsCommon {
        webLogo {
          originalId
          url
        }
      }
    }
  `)

  const { pathname } = useLocation();
  const currentPath = pathname;
  const web_logo = data.datoCmsCommon.webLogo.url;
  const [currentPage, setCurrentPage] = useState("live22");
  const [currentUrl, setCurrentUrl] = useState("");

  useMemo(() => {
    if (
      [
        "/",
        "/download",
        "/promotion",
        "/credit",
        "/contact",
        "/login",
        "/register",
        "/download/",
        "/promotion/",
        "/credit/",
        "/contact/",
        "/login/",
        "/register/",
      ].includes(currentPath)
    )
      setCurrentPage("live22");
    else setCurrentPage(currentPath.split("/")[1]);
  }, [currentPath]);

  useEffect(() => {
    if (
      ["anchorDownload", "promotion", "credit", "contact", "live22"].includes(
        currentPage
      )
    )
      setCurrentUrl("");
    else setCurrentUrl(currentPath.split("/")[1]);
  }, [currentPath]);

  return (
    <WrapperHeader theme={colorHeaderTheme[currentPage]}>
      <div className="lg:container sm:container mx-auto md:px-5">
        <WrapperContent theme={colorHeaderTheme[currentPage]}>
          <div className="w-full flex items-center justify-between absolute">
            <img className="logo" src={web_logo} alt="" />
            <div className="lg:block md:flex sm:flex m-5">
              <a
                href={`${currentUrl === ""
                  ? `${currentUrl}/login`
                  : `/${currentUrl}/login`
                  }`}
              >
                <WrapperButton className="cursor-pointer">
                  <Button
                    theme={colorHeaderTheme[currentPage]}
                    className={currentPath.includes('/login') ? "active whitespace-nowrap" : "whitespace-nowrap"}
                  >
                    เข้าสู่ระบบ
                  </Button>
                </WrapperButton>
              </a>
              <a
                href={`${currentUrl === ""
                  ? `${currentUrl}/register`
                  : `/${currentUrl}/register`
                  }`}
              >
                <WrapperButton className="cursor-pointer">
                  <Button
                    theme={colorHeaderTheme[currentPage]}
                    className={currentPath.includes('/register') ? "active whitespace-nowrap" : "whitespace-nowrap"}
                  >
                    สมัครสมาชิก
                  </Button>
                </WrapperButton>
              </a>
            </div>
          </div>
        </WrapperContent>
      </div>
    </WrapperHeader>
  );
};

export default Header;
